import { MessageSquareText, Send } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';

import { DEFAULT_TEMPLATE_VARIABLE_SAMPLE_VALUES } from '@apps/web/components/shared/messages/template_variable_metadata';
import { VariableTextEditor } from '@apps/web/components/shared/messages/variable_text_editor';
import { Button } from '@apps/web/components/ui/button';
import { Card } from '@apps/web/components/ui/card';
import { type CoverageArea } from '@apps/web/features/coverage/model/area_model';
import {
    ASK_AUDIENCE_DEFAULT_ON,
    ASK_AUDIENCE_LABELS,
    ASK_AUDIENCE_ORDER,
    type AskAudienceKey,
    askCandidateMatchesAudience,
    buildAskCandidates,
    resolveAskRecipients,
} from '@apps/web/features/coverage/model/ask_model';
import { type SlotAvailability } from '@apps/web/features/coverage/model/cell_model';
import {
    type CoverageRow,
    type WorkWeekStartDay,
} from '@apps/web/features/coverage/model/coverage_types';
import { type CoverageSelection } from '@apps/web/features/coverage/model/selection_model';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { getTemplateVariablesForFlow } from '@apps/web/lib/template_variables';
import { cn } from '@apps/web/lib/utils';
import { useTemplateVariableManifest } from '@apps/web/swr_data_fetching/data_fetching_hooks';

// Enough names to recognise the cohort without turning the rail into a list.
const CHIP_LIMIT = 5;

function initials(name: string): string {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    return (first + last).toUpperCase();
}

function shortName(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length < 2) {
        return name;
    }
    return `${parts[0]} ${parts[parts.length - 1]?.[0] ?? ''}.`;
}

function defaultMessage(selectionSentence: string | null): string {
    const slot = selectionSentence === null ? '' : ` ${selectionSentence}`;
    return (
        'Hi {caregiver_first_name}, we’re planning the schedule. ' +
        `Could you cover${slot} shifts? ` +
        'Reply with the days and times that work and I’ll save them to ' +
        'your availability.'
    );
}

export function CoverageAskComposer({
    rows,
    selection,
    selectionLabel,
    selectionSentence,
    organizationId,
    area,
    workWeekStartDay,
    onSent,
    onClear,
}: {
    readonly rows: ReadonlyArray<CoverageRow>;
    readonly selection: CoverageSelection;
    readonly selectionLabel: string | null;
    readonly selectionSentence: string | null;
    readonly organizationId: string;
    readonly area: CoverageArea;
    readonly workWeekStartDay: WorkWeekStartDay;
    readonly onSent: () => void;
    readonly onClear: () => void;
}) {
    const [enabledAudiences, setEnabledAudiences] = useState<
        ReadonlySet<AskAudienceKey>
    >(
        () =>
            new Set(
                ASK_AUDIENCE_ORDER.filter(
                    (audience) => ASK_AUDIENCE_DEFAULT_ON[audience],
                ),
            ),
    );
    const [message, setMessage] = useState(() =>
        defaultMessage(selectionSentence),
    );
    const [draftedFor, setDraftedFor] = useState(selectionSentence);
    const [isSending, setIsSending] = useState(false);

    // The rail stays mounted across slot clicks, so re-derive during render
    // and only while the text is still the untouched default.
    if (draftedFor !== selectionSentence) {
        setDraftedFor(selectionSentence);
        if (message === defaultMessage(draftedFor)) {
            setMessage(defaultMessage(selectionSentence));
        }
    }

    const { data: variableManifest } = useTemplateVariableManifest(
        organizationId,
        {
            reason: 'coverage-availability-ask-variables',
        },
    );
    const variables = useMemo(
        () =>
            getTemplateVariablesForFlow(
                variableManifest,
                'message_outreach_sms',
            ),
        [variableManifest],
    );

    const candidates = useMemo(
        () => buildAskCandidates(rows, selection, area, workWeekStartDay),
        [rows, selection, area, workWeekStartDay],
    );
    const recipients = useMemo(
        () => resolveAskRecipients(candidates, enabledAudiences),
        [candidates, enabledAudiences],
    );
    const { byAudience, availabilityCounts } = useMemo(() => {
        const byAudience = new Map<AskAudienceKey, Array<string>>();
        const availabilityCounts: Record<SlotAvailability, number> = {
            available: 0,
            unavailable: 0,
            unknown: 0,
        };
        for (const audience of ASK_AUDIENCE_ORDER) {
            byAudience.set(audience, []);
        }
        for (const candidate of candidates) {
            availabilityCounts[candidate.availability] += 1;
            for (const audience of ASK_AUDIENCE_ORDER) {
                if (askCandidateMatchesAudience(candidate, audience)) {
                    byAudience.get(audience)?.push(candidate.name);
                }
            }
        }
        return { byAudience, availabilityCounts };
    }, [candidates]);
    const askableElsewhere =
        resolveAskRecipients(candidates, new Set(ASK_AUDIENCE_ORDER)).length -
        recipients.length;

    async function send() {
        const trimmed = message.trim();
        if (trimmed === '' || recipients.length === 0 || isSending) {
            return;
        }
        setIsSending(true);
        let response;
        try {
            response = await getApiClient().post({
                url: '/organizations/{organization_id}/message-outreaches/availability-asks',
                pathParams: { organization_id: organizationId },
                body: {
                    message: trimmed,
                    caregiver_ids: recipients.map((r) => r.caregiverId),
                    // The grid never crosses the server's repeat-ask cap.
                    include_recently_asked: false,
                    // The cells clicked, not their cross product: two
                    // parallel lists would ask about slots nobody picked.
                    availability_ask_slots: (selection.kind === 'cells'
                        ? selection.cells
                        : []
                    ).map((cell) => ({
                        day_group: cell.group,
                        day_part: cell.dayPart,
                    })),
                },
            });
        } catch (error) {
            // The server rejects a selection asked too recently, and "try
            // again" would be advice that cannot work.
            const detail =
                error instanceof Error && error.message
                    ? error.message
                    : 'We couldn’t send that ask. Try again.';
            toast.error(detail, { persistNotification: false });
            setIsSending(false);
            return;
        }
        // Past this point the ask is sent. Nothing here may report failure,
        // or a retry texts every recipient a second time.
        try {
            // Count ids, not names: the server omits a skipped caregiver
            // from the name list when their record has no name.
            const skippedIds: ReadonlyArray<string> =
                response?.skipped_caregiver_ids ?? [];
            const recentlyAsked: ReadonlyArray<string> =
                response?.recently_asked_caregiver_ids ?? [];
            // The server's own count: it also drops anyone who left the
            // roster between the grid load and this send.
            const asked =
                response?.caregiver_count ??
                Math.max(
                    recipients.length -
                        skippedIds.length -
                        recentlyAsked.length,
                    0,
                );
            const reasons = [
                skippedIds.length > 0
                    ? `${skippedIds.length} on the do-not-contact list`
                    : null,
                recentlyAsked.length > 0
                    ? `${recentlyAsked.length} asked recently`
                    : null,
            ].filter((reason) => reason !== null);
            if (reasons.length > 0) {
                toast.message(
                    `Asked ${asked} ${asked === 1 ? 'caregiver' : 'caregivers'}. ` +
                        `Skipped ${reasons.join(' and ')}.`,
                    { persistNotification: false },
                );
            } else {
                toast.success(
                    `Asked ${asked} ${asked === 1 ? 'caregiver' : 'caregivers'}`,
                    { persistNotification: false },
                );
            }
            onSent();
            // The server holds the real guard; clearing keeps the rail
            // from inviting a second click before the row lands.
            onClear();
        } finally {
            setIsSending(false);
        }
    }

    return (
        <Card className="gap-3 p-4 @min-[1160px]/coverage-layout:sticky @min-[1160px]/coverage-layout:top-4">
            <div className="flex items-start gap-2">
                <MessageSquareText
                    className="text-primary mt-0.5 size-4 shrink-0"
                    aria-hidden="true"
                />
                <div className="min-w-0 grow">
                    <h2 className="truncate text-sm font-semibold">
                        {selectionLabel ?? 'Ask about availability'}
                    </h2>
                    <p className="text-muted-foreground text-xs">
                        {availabilityCounts.available} of {candidates.length}{' '}
                        available for at least one selected time
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onClear}
                    className="text-muted-foreground hover:text-foreground text-xs"
                >
                    Clear
                </button>
            </div>
            <dl
                aria-label="Availability for selected times"
                className="text-muted-foreground flex flex-wrap gap-3 text-xs"
            >
                <div className="flex gap-1">
                    <dt>Available</dt>
                    <dd>{availabilityCounts.available}</dd>
                </div>
                <div className="flex gap-1">
                    <dt>Unavailable</dt>
                    <dd>{availabilityCounts.unavailable}</dd>
                </div>
                <div className="flex gap-1">
                    <dt>Unknown</dt>
                    <dd>{availabilityCounts.unknown}</dd>
                </div>
            </dl>
            {recipients.length > 0 ? (
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-muted-foreground text-xs">To</span>
                    {recipients.slice(0, CHIP_LIMIT).map((recipient) => (
                        <span
                            key={recipient.caregiverId}
                            className="bg-muted flex min-w-0 items-center gap-1 rounded-full py-0.5 pr-2 pl-0.5 text-xs"
                        >
                            <span className="bg-primary/15 text-primary flex size-4 items-center justify-center rounded-full text-[9px] font-semibold">
                                {initials(recipient.name)}
                            </span>
                            <span className="truncate">
                                {shortName(recipient.name)}
                            </span>
                        </span>
                    ))}
                    {recipients.length > CHIP_LIMIT ? (
                        <span className="text-primary text-xs font-medium">
                            +{recipients.length - CHIP_LIMIT} more
                        </span>
                    ) : null}
                </div>
            ) : null}
            <fieldset
                className="space-y-1.5"
                aria-describedby="coverage-ask-audiences-help"
            >
                <legend className="text-xs font-medium">Who to text</legend>
                <p
                    id="coverage-ask-audiences-help"
                    className="text-muted-foreground text-xs"
                >
                    Missing information can include available caregivers. Each
                    person gets one text.
                </p>
                <div className="flex flex-wrap gap-1.5">
                    {ASK_AUDIENCE_ORDER.map((audience) => {
                        const count = (byAudience.get(audience) ?? []).length;
                        const isOn = enabledAudiences.has(audience);
                        return (
                            <button
                                key={audience}
                                type="button"
                                aria-pressed={isOn}
                                disabled={count === 0}
                                title={(byAudience.get(audience) ?? [])
                                    .slice(0, 6)
                                    .join(', ')}
                                onClick={() =>
                                    setEnabledAudiences((previous) => {
                                        const next = new Set(previous);
                                        if (next.has(audience)) {
                                            next.delete(audience);
                                        } else {
                                            next.add(audience);
                                        }
                                        return next;
                                    })
                                }
                                className={cn(
                                    'rounded-full border px-2.5 py-1 text-xs font-medium disabled:opacity-40',
                                    isOn
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-border text-muted-foreground',
                                )}
                            >
                                {isOn ? '✓ ' : ''}
                                {ASK_AUDIENCE_LABELS[audience]} · {count}
                            </button>
                        );
                    })}
                </div>
            </fieldset>
            <VariableTextEditor
                id="coverage-availability-ask"
                value={message}
                onChange={setMessage}
                variables={variables}
                variablePreviewValues={DEFAULT_TEMPLATE_VARIABLE_SAMPLE_VALUES}
                ariaLabel="Availability ask message"
            />
            {recipients.length === 0 ? (
                <p className="text-muted-foreground text-xs">
                    {askableElsewhere > 0
                        ? `No one in the audiences you picked. Turn one on to reach ${askableElsewhere} more.`
                        : 'Nobody here to ask.'}
                </p>
            ) : (
                <Fragment>
                    <Button
                        onClick={() => void send()}
                        disabled={isSending}
                        className="w-full"
                    >
                        <Send className="size-4" aria-hidden="true" />
                        {isSending
                            ? 'Sending…'
                            : `Text ${recipients.length} ${recipients.length === 1 ? 'caregiver' : 'caregivers'}`}
                    </Button>
                </Fragment>
            )}
        </Card>
    );
}
